import { useCallback } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTransition,
} from '@atlaskit/modal-dialog';
import { Text } from '@atlaskit/primitives';

import { useAnalytics } from 'src/components/settings/analytics';

import { useDeleteTestCase } from '../hooks/use-delete-test-case';
import { messages } from '../messages';

type RemoveTestCaseModalProps = {
  isOpen: boolean;
  testName: string;
  testUuid: string;
  onClose: () => void;
  onDeleteSuccess?: () => void;
};

export const RemoveTestCaseModal = ({
  isOpen,
  testName,
  testUuid,
  onClose,
  onDeleteSuccess,
}: RemoveTestCaseModalProps) => {
  const { publishButtonClickEvent } = useAnalytics({
    source: 'removeTestCaseModal',
  });

  const onSuccess = useCallback(() => {
    onDeleteSuccess?.();
    onClose();
  }, [onDeleteSuccess, onClose]);

  const { deleteTestCase, isDeletingTestCase } = useDeleteTestCase({
    testName,
    onSuccess,
  });

  const { formatMessage } = useIntl();

  const onRemoveConfirm = useCallback(() => {
    publishButtonClickEvent('confirmRemoveTestCase');
    deleteTestCase(testUuid);
  }, [publishButtonClickEvent, deleteTestCase, testUuid]);

  const onModalClose = useCallback(() => {
    if (isDeletingTestCase) return;

    onClose();
  }, [isDeletingTestCase, onClose]);

  return (
    <ModalTransition>
      {isOpen && (
        <Modal onClose={onModalClose} testId="test-case-remove-confirm-modal">
          <ModalHeader>
            <ModalTitle>
              {formatMessage(messages.removeTestModalTitle)}
            </ModalTitle>
          </ModalHeader>
          <ModalBody>
            <FormattedMessage
              {...messages.removeTestConfirmMessage}
              values={{
                testName: <Text weight="bold">{testName}</Text>,
              }}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              appearance="subtle"
              onClick={onModalClose}
              isDisabled={isDeletingTestCase}
            >
              {formatMessage(messages.removeTestCancelButton)}
            </Button>
            <Button
              appearance="primary"
              onClick={onRemoveConfirm}
              isLoading={isDeletingTestCase}
              autoFocus
            >
              {formatMessage(messages.removeTestConfirmButton)}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </ModalTransition>
  );
};
